/**
 * Analyzer: BE-Rule Impact (Wave A, v7.4.4) — MEASURE-ONLY
 *
 * The "move stop to breakeven at +1R" rule is gospel for Diaz / SMB,
 * and Rake Trades is adamantly against it. Both have track records.
 * Reasonable traders disagree. This analyzer's job is NOT to take a
 * side — it's to compute, from THIS trader's own data, which approach
 * actually preserves more winners FOR THEM, and surface that delta.
 *
 * The journal can't directly see whether the trader manually moved
 * their stop to BE — that action isn't recorded by JournifiSync. We
 * infer it from the exit outcome on trades that reached +1R favorable:
 *
 *   - Trade reached mfeR ≥ 1.0 AND
 *     - Final R ≥ +1.5R  → "Held the runner"        (DIDN'T move to BE)
 *     - Final R between -0.2R and +0.2R → "Stopped at BE"  (DID move to BE)
 *     - Final R ≤ -0.5R  → "Gave back to loss"      (DIDN'T move to BE)
 *
 *   The "scratch" zone is excluded from cohort assignment — too
 *   ambiguous between intentional BE and natural mid-trade exit.
 *
 * Then compute two outcome cohorts:
 *
 *   - "Moved to BE" cohort: trades that landed in "Stopped at BE"
 *     → represents BE-rule users when BE got hit
 *   - "Held without BE" cohort: trades that landed in "Held runner" + "Gave back"
 *     → represents NOT moving to BE
 *
 *   For each cohort, compute the conserved-winner rate (% that ended +R)
 *   and the avg R. If the delta between the two cohorts exceeds 15%,
 *   surface an observation showing the trader THEIR own data.
 *
 * Output framing is always neutral — the data speaks. No judgment,
 * no recommended action beyond "this is what happened to you."
 *
 * Window: rolling 60 trading days (BE rule needs more samples than
 * a typical ratio analyzer because it segments into three buckets).
 *
 * Reads:
 *   trade.date, trade.rMultiple, trade.barAnalysis.mfeR
 *
 * Returns:
 *   - null  when sample sizes too small OR delta < 15% (don't nag)
 *   - Observation  when the data clearly favors one approach for THIS trader
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { getTradingDay, addDaysToDateStr } from '../../../timeService.js';

export const key = 'beRuleImpact';
export const tier = 'free';

// Classification thresholds — see file header for rationale.
const REACHED_1R_THRESHOLD     = 1.00;   // mfeR ≥ this counts as "had a runner"
const BE_BAND_LOWER            = -0.20;  // final R between [BE_BAND_LOWER, BE_BAND_UPPER]
const BE_BAND_UPPER            = 0.20;   //   counts as "stopped at BE"
const RUNNER_MIN_FINAL_R       = 1.50;   // final R ≥ this counts as "held to target"
const GAVEBACK_MAX_FINAL_R     = -0.50;  // final R ≤ this counts as "gave back to loss"

// Sample-size guards
const MIN_PER_COHORT           = 5;
const MIN_PROACTIVE_DELTA      = 0.15;   // 15% delta in conserved-winner rate to fire

const inWindow = (trade, fromDateStr, toDateStr) => {
  if (!trade?.date || typeof trade.date !== 'string') return false;
  return trade.date >= fromDateStr && trade.date <= toDateStr;
};

/**
 * Inspect a trade's outcome shape to assign it to a BE cohort, OR null
 * if it doesn't qualify (didn't reach 1R, missing data, ambiguous zone).
 */
const cohortOf = (trade) => {
  const mfeR = trade?.barAnalysis?.mfeR;
  if (!Number.isFinite(mfeR) || mfeR < REACHED_1R_THRESHOLD) return null;
  const finalR = parseFloat(trade?.rMultiple);
  if (!Number.isFinite(finalR)) return null;

  if (finalR >= BE_BAND_LOWER && finalR <= BE_BAND_UPPER) return 'movedToBE';
  if (finalR >= RUNNER_MIN_FINAL_R) return 'heldNoBE';
  if (finalR <= GAVEBACK_MAX_FINAL_R) return 'gaveBackNoBE';
  return null;  // mid-zone — too ambiguous to classify
};

/**
 * Compute summary stats for a cohort.
 *   conservedRate: % of trades that ended at ≥ 0R (didn't fully give back)
 *   avgR: mean rMultiple in the cohort
 */
const summarize = (trades) => {
  if (trades.length === 0) return { count: 0, conservedRate: 0, avgR: 0 };
  const conserved = trades.filter(t => parseFloat(t.rMultiple) >= 0).length;
  const sumR = trades.reduce((s, t) => s + parseFloat(t.rMultiple), 0);
  return {
    count: trades.length,
    conservedRate: conserved / trades.length,
    avgR: sumR / trades.length,
  };
};

const fmtPct = (n) => `${Math.round(n * 100)}%`;
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

export const analyze = (ctx) => {
  const { allTrades, currentTradingDay, windowDays = 60 } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;

  const anchor = currentTradingDay || getTradingDay(Date.now()) || null;
  if (!anchor) return null;
  const fromDateStr = addDaysToDateStr(anchor, -windowDays);

  const movedToBE = [];
  const heldNoBE = [];
  const gaveBackNoBE = [];

  for (const t of allTrades) {
    if (!inWindow(t, fromDateStr, anchor)) continue;
    const c = cohortOf(t);
    if (c === 'movedToBE') movedToBE.push(t);
    else if (c === 'heldNoBE') heldNoBE.push(t);
    else if (c === 'gaveBackNoBE') gaveBackNoBE.push(t);
  }

  // Both cohorts must have minimum samples for a meaningful comparison.
  // The "no-BE" cohort combines runners + give-backs since those represent
  // the population that didn't move to BE.
  const noBE = [...heldNoBE, ...gaveBackNoBE];
  if (movedToBE.length < MIN_PER_COHORT || noBE.length < MIN_PER_COHORT) return null;

  const beStats = summarize(movedToBE);
  const noBeStats = summarize(noBE);

  // Conserved-winner rate delta: how often did each approach preserve a non-loss?
  const delta = noBeStats.conservedRate - beStats.conservedRate;
  // Avg R delta — secondary signal, useful for the narrative
  const avgRDelta = noBeStats.avgR - beStats.avgR;

  // Don't fire if delta is below the proactive threshold — the data is
  // inconclusive, no point nagging.
  if (Math.abs(delta) < MIN_PROACTIVE_DELTA) return null;

  // The framing: which approach worked better for THIS trader?
  const favorsNoBE = delta > 0;
  const winnerLabel = favorsNoBE ? 'NOT moving to BE' : 'moving to BE';
  const loserLabel  = favorsNoBE ? 'moving to BE' : 'NOT moving to BE';
  const winnerStats = favorsNoBE ? noBeStats : beStats;
  const loserStats  = favorsNoBE ? beStats   : noBeStats;
  const winnerLabel2 = favorsNoBE ? 'no-BE'  : 'moved-to-BE';

  // Severity: INFO when the data is mildly suggestive; WARNING when strongly so.
  const severity = Math.abs(delta) >= 0.25 ? SEVERITY.WARNING : SEVERITY.INFO;

  return makeObservation({
    id: `${key}:${anchor}`,
    analyzer: key,
    scope: 'progress',
    scopeId: anchor,
    severity,
    category: CATEGORY.PROCESS,
    title: `Your data: ${winnerLabel} preserved ${fmtPct(Math.abs(delta))} more winners`,
    detail: `Over ${windowDays}D, trades that reached +1R: when you ${loserLabel} → ${fmtPct(loserStats.conservedRate)} preserved at ≥ 0R (avg ${fmtR(loserStats.avgR)}). When you ${winnerLabel} → ${fmtPct(winnerStats.conservedRate)} preserved (avg ${fmtR(winnerStats.avgR)}). Δ ${fmtPct(Math.abs(delta))} favors ${winnerLabel2}.`,
    action: `Reasonable traders disagree on the BE rule (Diaz/SMB for it, Rake against). Your own ${windowDays}D data points to ${winnerLabel2.toUpperCase()} being the better approach FOR YOU. Re-run after another ${Math.max(0, 2 * MIN_PER_COHORT - Math.min(movedToBE.length, noBE.length))} more 1R+ trades to confirm.`,
    evidence: {
      cohorts: {
        movedToBE: beStats,
        heldNoBE: summarize(heldNoBE),
        gaveBackNoBE: summarize(gaveBackNoBE),
        noBeCombined: noBeStats,
      },
      delta,
      avgRDelta,
      windowDays,
      thresholds: {
        reached1R: REACHED_1R_THRESHOLD,
        beBand: [BE_BAND_LOWER, BE_BAND_UPPER],
        runnerMin: RUNNER_MIN_FINAL_R,
        gaveBackMax: GAVEBACK_MAX_FINAL_R,
      },
    },
    confidence: Math.min(0.9, 0.5 + Math.min(movedToBE.length, noBE.length) / 40),
    priority: severity === SEVERITY.WARNING ? 78 : 62,
  });
};
